/**
 * useModuleGuard — T20
 *
 * Returns a structured guard object for a single licensed module. Designed for
 * use in page components that need to conditionally disable write actions and
 * show the ReadOnlyModuleBanner.
 *
 * Reads the module cap via a single Zustand selector (no intermediate hook),
 * so only one subscription is created per call site. The return object is
 * memoized on `cap` to avoid new object references on every render.
 *
 * Relationship to useEntitlements: useEntitlements exposes `isModuleReadonly(m)`
 * which returns the same boolean as `isReadonly` here, plus `getModuleCap(m)`
 * which equals `cap`. This hook additionally exposes `canWrite` (FULL-only) and
 * packages all three into a single stable object per module.
 *
 * Usage:
 *   const { isReadonly, canWrite } = useModuleGuard('forms');
 *   {isReadonly && <ReadOnlyModuleBanner />}
 *   <Button disabled={!canWrite}>Add Client</Button>
 */

import { useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import type { LicensedModule, ModuleCap } from '../stores/authStore';

export interface ModuleGuard {
  /** The raw license cap returned by the backend: 'FULL' | 'READ' | 'NONE' */
  cap: ModuleCap;
  /**
   * True when cap === 'READ'.
   * The module is accessible at read-only level — show the banner and disable
   * primary write actions (create / edit / delete / submit).
   */
  isReadonly: boolean;
  /**
   * True when cap === 'FULL'.
   * The user has unrestricted write access — do not show the banner.
   */
  canWrite: boolean;
}

/**
 * Returns a guard object for `module`.
 *
 * Note: When cap === 'NONE' both `isReadonly` and `canWrite` are false.
 * NONE means the module is not included in the plan at all. In practice the
 * ProtectedRoute / navigation already hides the page — but components can
 * use this as an extra safety net.
 */
export function useModuleGuard(module: LicensedModule): ModuleGuard {
  // Direct single-selector subscription — avoids the 3 extra subscriptions
  // that useEntitlements() would create (hasModule, isModuleReadonly, refreshUser).
  const cap = useAuthStore((state) => state.getModuleCap(module));

  // Memoize so the returned object reference is stable when cap hasn't changed.
  // This preserves future React.memo optimizations on children that receive
  // isReadonly/canWrite as props.
  return useMemo(
    () => ({ cap, isReadonly: cap === 'READ', canWrite: cap === 'FULL' }),
    [cap],
  );
}
